// client/src/components/admin/VerificationReviewDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Card,
  CardMedia,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  DocumentScanner,
  CameraAlt
} from '@mui/icons-material';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`verification-tabpanel-${index}`}
      aria-labelledby={`verification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const VerificationReviewDialog = ({ open, onClose, user, onVerificationUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && user && user.id) {
      fetchVerificationData();
    }
  }, [open, user]);



  const fetchVerificationData = async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('adminToken');
                
                if (!token) {
                setError('Admin token not found');
                return;
                }

                const response = await fetch(
                `https://propertyearn.net/api/api/admin/users/${user.id}/verification`,
                {
                    headers: {
                    'Authorization': `Bearer ${token}`
                    }
                }
                );

                if (response.status === 404) {
                // No verification data found
                setVerificationData({ verification_status: 'not_started' });
                return;
                }

                if (!response.ok) {
                throw new Error(`Failed to fetch verification data: ${response.status}`);
                }

                const data = await response.json();
                setVerificationData(data);
            } catch (err) {
                setError(err.message);
                setVerificationData({ verification_status: 'error' });
            } finally {
                setLoading(false);
            }
            };

  const handleApprove = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `https://propertyearn.net/api/api/admin/users/${user.id}/verification/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ verification_level: 'verified' })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve verification');
      }

      onVerificationUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `https://propertyearn.net/api/api/admin/users/${user.id}/verification/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ rejection_reason: rejectionReason })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reject verification');
      }

      onVerificationUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Review Verification - {user.name}
        <Chip 
          label={user.verification_status || 'unknown'} 
          color={
            user.verification_status === 'approved' ? 'success' :
            user.verification_status === 'rejected' ? 'error' :
            user.verification_status === 'pending' ? 'warning' : 'default'
          }
          sx={{ ml: 2 }}
        />
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        ) : verificationData ? (
          <Box>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab icon={<DocumentScanner />} label="Documents" />
              <Tab icon={<CameraAlt />} label="Selfie" />
              <Tab icon={<Visibility />} label="Review" />
            </Tabs>


                <TabPanel value={activeTab} index={0}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                        Document Front
                        {!verificationData.documents?.document_front_url && (
                        <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />
                        )}
                    </Typography>
                    {verificationData.documents?.document_front_url ? (
                        <Card>
                        <CardMedia
                            component="img"
                            height="400"
                            image={`https://propertyearn.net${verificationData.documents.document_front_url}`}
                            alt="Document Front"
                            sx={{ objectFit: 'contain' }}
                            onError={(e) => {
                            console.error('Failed to load document front image');
                            e.target.style.display = 'none';
                            }}
                        />
                        </Card>
                    ) : (
                        <Typography color="error">No document front uploaded</Typography>
                    )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                        Document Back
                        {!verificationData.documents?.document_back_url && (
                        <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />
                        )}
                    </Typography>
                    {verificationData.documents?.document_back_url ? (
                        <Card>
                        <CardMedia
                            component="img"
                            height="400"
                            image={`https://propertyearn.net${verificationData.documents.document_back_url}`}
                            alt="Document Back"
                            sx={{ objectFit: 'contain' }}
                            onError={(e) => {
                            console.error('Failed to load document back image');
                            e.target.style.display = 'none';
                            }}
                        />
                        </Card>
                    ) : (
                        <Typography color="error">No document back uploaded</Typography>
                    )}
                    </Grid>
                </Grid>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                <Typography variant="h6" gutterBottom>
                    Selfie Verification
                    {!verificationData.documents?.selfie_url && (
                    <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />
                    )}
                </Typography>
                {verificationData.documents?.selfie_url ? (
                    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
                    <CardMedia
                        component="img"
                        height="400"
                        image={`https://propertyearn.net${verificationData.documents.selfie_url}`}
                        alt="Selfie Photo"
                        sx={{ objectFit: 'cover' }}
                        onError={(e) => {
                        console.error('Failed to load selfie image');
                        e.target.style.display = 'none';
                        }}
                    />
                    </Card>
                ) : (
                    <Typography color="error">No selfie uploaded</Typography>
                )}
                </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Typography variant="h6" gutterBottom>
                Verification Decision
              </Typography>
              
              <TextField
                fullWidth
                label="Rejection Reason (if rejecting)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                multiline
                rows={3}
                placeholder="Provide detailed reason for rejection..."
                sx={{ mb: 3 }}
              />

              <Alert severity="info" sx={{ mb: 2 }}>
                Please carefully review all documents before making a decision. 
                This action will be logged for audit purposes.
              </Alert>
            </TabPanel>
          </Box>
        ) : (
          <Typography>No verification data available</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleReject} 
          color="error"
          variant="outlined"
          disabled={loading || !rejectionReason.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <Cancel />}
        >
          {loading ? 'Rejecting...' : 'Reject'}
        </Button>
        <Button 
          onClick={handleApprove} 
          color="success"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
        >
          {loading ? 'Approving...' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerificationReviewDialog;